/**
 * GTM Consent Queue
 *
 * Simple override of window.dataLayer.push to queue events until flush_events is received.
 */

// Events to queue until flush_events is received (configurable)
const EVENTS_TO_QUEUE = [
	"page_view",
	"pageview",
	"view_item",
	"view_item_list",
	"add_to_cart",
	"remove_from_cart",
	"add_payment_info",
	"add_shipping_info",
	"begin_checkout",
	"purchase",
	"user_engagement",
	"language",
	"userId",
	"view_search_results",
];

// Events that should never be queued (critical GTM events)
const CRITICAL_EVENTS = [
	"gtm.js",
	"gtm.start",
	"gtm.load",
	"gtm.dom",
	"gtm.consent",
	"consent_update",
	"gtm.*",
];

interface GTMEvent {
	event: string;
	[key: string]: unknown;
}

export class GTMConsentQueue {
	private queue: GTMEvent[] = [];
	private consentReceived = false;
	private originalPush: ((...args: Object[]) => number) | null = null;
	private maxQueueSize = 50;

	constructor() {
		this.initializeDataLayerOverride();
	}

	private initializeDataLayerOverride() {
		// // Wait for dataLayer to be available
		// const checkDataLayer = () => {
		// 	if (typeof window !== "undefined" && window.dataLayer) {
		// 		this.setupOverride();
		// 	} else {
		// 		// Check again in next tick
		// 		setTimeout(checkDataLayer, 1);
		// 	}
		// };

		// checkDataLayer();
		window.dataLayer ??= [];
		this.setupOverride();
	}

	private setupOverride() {
		if (!window.dataLayer || this.originalPush) return;

		// Store original push method
		this.originalPush = window.dataLayer.push.bind(window.dataLayer);

		// Override dataLayer.push
		window.dataLayer.push = (...args: unknown[]) => {
			const events = args.filter(
				(arg): arg is GTMEvent => typeof arg === "object" && arg !== null && "event" in arg,
			);

			for (const event of events) {
				if (this.shouldQueueEvent(event)) {
					this.queueEvent(event);
				} else {
					// Pass through immediately
					this.originalPush!(event);
				}

				// Check if this is a flush trigger event
				if (this.isFlushTriggerEvent(event)) {
					this.onConsentReceived();
				}
			}

			// Handle non-event arguments (pass through immediately)
			const nonEvents = args.filter(
				(arg) => typeof arg !== "object" || arg === null || !("event" in arg),
			);

			if (nonEvents.length > 0 && this.originalPush) {
				return this.originalPush(...(nonEvents as Object[]));
			}

			return args.length;
		};

		console.log("[GTM Consent Queue] DataLayer override initialized");
	}

	private shouldQueueEvent(event: GTMEvent): boolean {
		const eventName = event.event;

		// Never queue critical events
		if (
			CRITICAL_EVENTS.includes(eventName) ||
			CRITICAL_EVENTS.some((e) => e.endsWith("*") && eventName.startsWith(e.slice(0, -1)))
		) {
			return false;
		}

		// If flush_events already received, don't queue
		if (this.consentReceived) {
			return false;
		}

		// Queue if event is in the queue list
		return EVENTS_TO_QUEUE.includes(eventName);
	}

	private isFlushTriggerEvent(event: GTMEvent): boolean {
		const eventName = event.event;
		return eventName === "flush_events";
	}

	private queueEvent(event: GTMEvent) {
		// Prevent queue from growing too large
		if (this.queue.length >= this.maxQueueSize) {
			console.warn("[GTM Consent Queue] Queue full, removing oldest event");
			this.queue.shift();
		}

		this.queue.push(event);
		console.log(`[GTM Consent Queue] Queued event: ${event.event} (${this.queue.length} in queue)`);
	}

	private onConsentReceived() {
		if (this.consentReceived) return;

		this.consentReceived = true;
		console.log(
			`[GTM Consent Queue] flush_events received, flushing ${this.queue.length} queued events`,
		);

		// Flush all queued events
		const eventsToFlush = [...this.queue];
		this.queue = [];

		// Add 1ms delay to ensure that the events are flushed after other gtm callstack events
		setTimeout(() => {
			eventsToFlush.forEach((event) => {
				if (window.dataLayer?.push) {
					console.log(`[GTM Consent Queue] Flushing: ${event.event}`);
					window.dataLayer?.push(event);
				}
			});
		}, 1);
	}

	// Debug methods (available globally)
	public getQueueStatus() {
		return {
			queueLength: this.queue.length,
			consentReceived: this.consentReceived,
			queuedEvents: this.queue.map((e) => e.event),
		};
	}

	public forceFlush() {
		console.log("[GTM Consent Queue] Forcing flush");
		this.onConsentReceived();
	}
}

// Export configuration for potential customization
export { EVENTS_TO_QUEUE, CRITICAL_EVENTS };
